/**
 * BillingSettings Component
 *
 * Displays subscription status, usage, and billing management options.
 * Integrates with Stripe for checkout and customer portal.
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/components/auth/ClerkAuthProvider';
import { useTenantCompat as useTenant } from '@/hooks/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { UsageIndicator } from '@/components/billing/UsageIndicator';
import { UpgradeModal } from '@/components/billing/UpgradeModal';
import { BillingCreditSection } from '@/components/billing/BillingCreditSection';
import { CreditUsageHistory } from '@/components/billing/CreditUsageHistory';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || '';
const FREE_TIER_LIMIT = 25;

export default function BillingSettings() {
  const { tenantId } = useAuthContext();
  const { getToken } = useAuth();
  const { data: tenant, isLoading, refetch } = useTenant(tenantId ?? undefined);
  const [searchParams, setSearchParams] = useSearchParams();

  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Handle success/cancel from Stripe checkout
  useEffect(() => {
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (success === 'true') {
      setSuccessMessage('Your subscription has been activated! Thank you for upgrading to Pro.');
      // Refetch tenant data to show updated subscription
      refetch();
      // Clear URL params
      setSearchParams({});
    } else if (canceled === 'true') {
      // Just clear the params, no message needed
      setSearchParams({});
    }
  }, [searchParams, refetch, setSearchParams]);

  const handleOpenBillingPortal = async () => {
    setIsPortalLoading(true);

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/stripe/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to open billing portal');
      }

      // Redirect to Stripe Customer Portal
      if (data.data?.url) {
        window.location.href = data.data.url;
      }
    } catch (err) {
      console.error('Billing portal error:', err);
      // Could show a toast here
    } finally {
      setIsPortalLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isPro = tenant?.subscription_tier === 'pro' && tenant?.subscription_status === 'active';
  const isTrial = tenant?.subscription_status === 'trial';
  const isPastDue = tenant?.subscription_status === 'past_due';
  const hasStripeCustomer = !!tenant?.stripe_customer_id;

  // Calculate trial days remaining
  const trialDaysRemaining = (() => {
    if (!isTrial || !tenant?.trial_ends_at) return null;
    const now = new Date();
    const trialEnd = tenant.trial_ends_at.toDate
      ? tenant.trial_ends_at.toDate()
      : new Date(tenant.trial_ends_at);
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  })();

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Subscription & Billing
          </CardTitle>
          <CardDescription>Manage your plan and billing information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Success Message */}
          {successMessage && (
            <div className="flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-700 dark:bg-green-950/30 dark:text-green-200">
              <CheckCircle2 className="h-4 w-4" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Past Due Warning */}
          {isPastDue && (
            <div className="flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" />
              <span>
                Your payment failed. Please update your payment method to avoid service interruption.
              </span>
            </div>
          )}

          {/* Current Plan */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <p className="font-medium capitalize">{tenant?.subscription_tier} Plan</p>
                <Badge
                  variant="outline"
                  className={cn(
                    isPro && 'border-primary bg-primary/10 text-primary',
                    isTrial && 'border-amber-500 bg-amber-50 text-amber-600',
                    isPastDue && 'border-destructive text-destructive'
                  )}
                >
                  {isPro && <Sparkles className="mr-1 h-3 w-3" />}
                  {tenant?.subscription_status === 'active' && 'Active'}
                  {tenant?.subscription_status === 'trial' && 'Trial'}
                  {tenant?.subscription_status === 'past_due' && 'Past Due'}
                  {tenant?.subscription_status === 'canceled' && 'Canceled'}
                </Badge>
              </div>
              {isPro && (
                <p className="text-sm text-muted-foreground">$97/month • Billed monthly</p>
              )}
              {isTrial && trialDaysRemaining !== null && (
                <p className="text-sm text-muted-foreground">
                  {trialDaysRemaining === 0
                    ? 'Trial has expired'
                    : `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} remaining in trial`}
                </p>
              )}
            </div>
          </div>

          <Separator />

          {/* Usage Indicator */}
          <div>
            <h3 className="mb-3 font-medium">Usage This Month</h3>
            <UsageIndicator
              currentUsage={tenant?.generations_this_month || 0}
              limit={isPro ? null : FREE_TIER_LIMIT}
              tier={tenant?.subscription_tier || 'free'}
              status={tenant?.subscription_status || 'trial'}
              trialEndsAt={
                tenant?.trial_ends_at
                  ? tenant.trial_ends_at.toDate
                    ? tenant.trial_ends_at.toDate()
                    : new Date(tenant.trial_ends_at)
                  : null
              }
              showUpgradeButton={!isPro}
            />
          </div>

          <Separator />

          {/* Plan Management */}
          <div className="space-y-3">
            <h3 className="font-medium">Plan Management</h3>

            {isPro ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  You're on the Pro plan with unlimited AI generations. Manage your subscription,
                  update payment methods, or view invoices in the billing portal.
                </p>
                {hasStripeCustomer && (
                  <Button
                    variant="outline"
                    onClick={handleOpenBillingPortal}
                    disabled={isPortalLoading}
                  >
                    {isPortalLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Opening...
                      </>
                    ) : (
                      <>
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Manage Billing
                      </>
                    )}
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Upgrade to Pro for unlimited AI-generated designs and take your lead conversion to
                  the next level.
                </p>
                <div className="flex items-center gap-3">
                  <Button onClick={() => setUpgradeModalOpen(true)}>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Upgrade to Pro
                  </Button>
                  {hasStripeCustomer && (
                    <Button
                      variant="outline"
                      onClick={handleOpenBillingPortal}
                      disabled={isPortalLoading}
                    >
                      {isPortalLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Opening...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="mr-2 h-4 w-4" />
                          Billing Portal
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Billing History */}
          <div className="space-y-3">
            <h3 className="font-medium">Billing History</h3>
            {hasStripeCustomer ? (
              <p className="text-sm text-muted-foreground">
                View your past invoices and payment history in the{' '}
                <button
                  onClick={handleOpenBillingPortal}
                  disabled={isPortalLoading}
                  className="font-medium text-primary underline underline-offset-2 hover:no-underline disabled:opacity-50"
                >
                  billing portal
                </button>
                .
              </p>
            ) : (
              <div className="rounded-lg border-2 border-dashed border-muted py-6 text-center">
                <p className="text-sm text-muted-foreground">No billing history available</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Billing history will appear here after your first payment
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <UpgradeModal
        open={upgradeModalOpen}
        onOpenChange={setUpgradeModalOpen}
        reason={
          trialDaysRemaining === 0
            ? 'trial_expired'
            : (tenant?.generations_this_month || 0) >= FREE_TIER_LIMIT
              ? 'limit_reached'
              : 'upgrade'
        }
        currentUsage={tenant?.generations_this_month || 0}
        usageLimit={FREE_TIER_LIMIT}
      />

      {/* Credit Management Section */}
      <BillingCreditSection
        monthlyCredits={tenant?.generation_credits_monthly || 0}
        addonCredits={tenant?.generation_credits_addon || 0}
        rolloverCredits={tenant?.generation_credits_rollover || 0}
        isPro={isPro}
      />

      {/* Credit Usage History */}
      <CreditUsageHistory />
    </div>
  );
}
